import type { InternalActionDef, VisibilityCondition } from '../core/schema/types';
import { ActionRegistry, type ActionableResult } from '../registry/ActionRegistry';
import { evaluateCondition } from './evaluateCondition';

/**
 * Unified evaluation pipeline for action visibility and enabled state.
 *
 * 4-step priority order:
 *  0. Action.isAvailable()  → OOP layer: global gate (permissions, feature flags)
 *  1. Permission check       → structural layer: if denied, invisible
 *  2. visibilityCondition    → structural layer: if fails, invisible
 *  3. disabledWhen           → structural layer: if matches, visible but disabled
 *  4. Action.isActionable()  → OOP layer: row-level gate
 *
 * Step 0 is the OOP global gate — returns false means the action is hidden for ALL rows.
 * Steps 1-3 are schema-driven structural checks.
 * Step 4 is the OOP row-level check.
 */
export function evaluateIsActionable(
  namespace: string,
  actionDef: InternalActionDef,
  row: Record<string, unknown>,
  checkPermission?: (perm: { action: string; resource: string }) => boolean,
): ActionableResult {
  // Step 0: OOP Action.isAvailable() — global gate
  const action = ActionRegistry.resolveAction(actionDef.id);
  if (action && !action.isAvailable()) {
    return { visible: false, enabled: false };
  }

  // Step 1: Permission check (structural)
  if (actionDef.permission) {
    const resolver = checkPermission ?? ActionRegistry.getPermissionResolver();
    if (resolver && !resolver(actionDef.permission)) {
      return { visible: false, enabled: false };
    }
  }

  // Step 2: Schema visibilityCondition (structural)
  if (actionDef.visibilityCondition && !evaluateCondition(actionDef.visibilityCondition, row)) {
    return { visible: false, enabled: false };
  }

  // Step 3: Schema disabledWhen (structural)
  if (actionDef.disabledWhen && evaluateCondition(actionDef.disabledWhen, row)) {
    return {
      visible: true,
      enabled: false,
      reason: `${actionDef.label} is not available for this item`,
    };
  }

  // Step 4: OOP Action.isActionable() — row-level gate
  if (action) {
    const result = action.isActionable(row);
    if (!result.visible || !result.enabled) return result;
  }

  return { visible: true, enabled: true };
}
